import {
  Button,
  Flex,
  Modal,
  NumberInput,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { omit } from "lodash";
import { useCallback } from "react";

import { UpdateInvoiceInput } from "../../../graphql/generated.ts";
import { Invoice } from "../../../types/invoice/invoice.ts";
import { parseStringToNumber, priceFormatter } from "../../../utils/number.ts";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal.tsx";
import Calendar from "../../Icons/Calendar.tsx";

interface Props {
  data: Invoice;
  opened?: boolean;
  onClose: () => void;
  onSubmit: (value: UpdateInvoiceInput) => void;
}

export const UpdateInvoiceModal = ({
  data,
  opened = false,
  onClose,
  onSubmit,
}: Props) => {
  const [
    confirmationModalOpened,
    { open: openConfirmationModal, close: closeConfirmationModal },
  ] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      invoiceNumber: data.invoiceNumber,
      customerName: data.customer.name,
      totalInvoice: data.totalInvoice && Number(data.totalInvoice),
      invoiceDueDate: data.invoiceDueDate && new Date(data.invoiceDueDate),
      totalPaid: data.totalPaid && Number(data.totalPaid),
      lastPaymentDate:
        (data.lastPaymentDate && new Date(data.lastPaymentDate)) ??
        (data.lastPayment?.paidAt && new Date(data.lastPayment?.paidAt)),
    },
    validate: {
      totalInvoice: isNotEmpty("Field is required"),
      invoiceDueDate: isNotEmpty("Field is required"),
      totalPaid: isNotEmpty("Field is required"),
      lastPaymentDate: isNotEmpty("Field is required"),
    },
    validateInputOnBlur: true,
    clearInputErrorOnChange: true,
  });

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }

    onSubmit(omit(form.values, "invoiceNumber", "customerName"));
  }, [form, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title={"Update Invoice"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <TextInput
            size="m"
            label="Invoice Number"
            disabled={true}
            {...form.getInputProps("invoiceNumber")}
          />
          <TextInput
            size="m"
            label="Customer Name"
            disabled={true}
            {...form.getInputProps("customerName")}
          />
          <NumberInput
            required
            label="Total Invoice Amount"
            placeholder="Enter Total Invoice Amount"
            size="m"
            precision={2}
            parser={parseStringToNumber}
            formatter={priceFormatter}
            min={0}
            hideControls
            {...form.getInputProps("totalInvoice")}
          />
          <DatePickerInput
            required
            label="Invoice Due Date"
            placeholder="Select Invoice Due Date"
            icon={<Calendar />}
            firstDayOfWeek={0}
            size="m"
            valueFormat="YYYY MMM DD"
            popoverProps={{ withinPortal: true }}
            {...form.getInputProps("invoiceDueDate")}
          />
          <NumberInput
            required
            label="Total Paid Amount"
            placeholder="Enter Total Paid Amount"
            size="m"
            precision={2}
            parser={parseStringToNumber}
            formatter={priceFormatter}
            min={0}
            hideControls
            {...form.getInputProps("totalPaid")}
          />
          <DatePickerInput
            required
            label="Last Payment Date"
            placeholder="Select Last Payment Date"
            icon={<Calendar />}
            firstDayOfWeek={0}
            size="m"
            valueFormat="YYYY MMM DD"
            popoverProps={{ withinPortal: true }}
            {...form.getInputProps("lastPaymentDate")}
          />
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid() || !form.isDirty()}
              onClick={openConfirmationModal}
            >
              save
            </Button>
          </Flex>
        </Flex>
      </Modal>
      {confirmationModalOpened && (
        <ConfirmationModal
          opened={true}
          message={"Are you sure you want to update this Invoice data?"}
          onConfirm={handleSubmit}
          onClose={closeConfirmationModal}
        />
      )}
    </>
  );
};
